import styled from '@emotion/styled'
import divider from '../../../assets/images/divider.svg'
import Box from './Box'
import { SIXTEEN_PX, TEN_PX } from '../enums/Spacings'
import { BorderRadixes } from '../enums/BorderRadixes'
import RegisterStep from './RegisterStep'
import { memo, useMemo } from 'react'
import { getRegisterSteps } from '../helpers/RegisterStepsHeaderHelper'
import { State } from '../hooks/useAppState'
import { Activity, EnrollmentPaymentStatus } from '../../../pure-js/types/GrooverTypes'
import { isActivityFree } from '../utils/activityUtils'

const PAYMENT_STEP_NUMBER = 4

type StepsHeaderProps = {
  state: State
  activity: Activity
  activeRegisterStep: number
  isRegistrationCompleted: boolean
  onOpenStep: (step: number) => void
}

const RegisterStepsHeader = ({
  state,
  activity,
  activeRegisterStep,
  isRegistrationCompleted,
  onOpenStep
}: StepsHeaderProps) => {
  const registerSteps = useMemo(() => getRegisterSteps(activeRegisterStep), [activeRegisterStep])
  const isFreeEvent = useMemo(
    () => isActivityFree(activity, state.selectedActivities),
    [activity, state.selectedActivities]
  )

  // Tabs are clickable only when the registration or payment is fully completed.
  // Using obfuscatedEnrollment.mainUser alone would also enable tabs for pending/unpaid
  // return-via-URL enrollments, allowing users to navigate to the payment step before approval.
  const isEffectivelyCompleted =
    isRegistrationCompleted ||
    state.isPaid ||
    [EnrollmentPaymentStatus.Paid, EnrollmentPaymentStatus.PartlyPaid].includes(
      state.obfuscatedEnrollment?.paymentStatus
    )

  return (
    <StepsHeader fullWidth fullPadding spacing={SIXTEEN_PX} direction="row" align="center" justify="space-between">
      {registerSteps.map((step, index) => {
        const stepNumber = index + 1
        const isPaymentStep = stepNumber === PAYMENT_STEP_NUMBER
        return (
          <Box key={`registerStep-${index}`} direction="row" align="center" gap={TEN_PX}>
            <RegisterStep
              index={index}
              step={step}
              stepNumber={stepNumber}
              isRegistrationCompleted={isEffectivelyCompleted}
              isCurrentlyOpen={stepNumber === activeRegisterStep}
              isDisabled={isPaymentStep && isFreeEvent}
              onOpenStep={onOpenStep}
            />
            {index < registerSteps.length - 1 && <StepDivider src={divider} />}
          </Box>
        )
      })}
    </StepsHeader>
  )
}

const StepsHeader = styled(Box)`
  background: #f3f5fa;
  border-radius: ${BorderRadixes.small};
`

const StepDivider = styled.img`
  height: 30px;
  max-width: 50px;

  @media (max-width: 818px) {
    display: none;
  }
`

export default memo(RegisterStepsHeader)
